// Mirror of backend/money.py — integer minor units, round half up.
export const DISCOUNT_ERR = "Discount can't exceed the amount";
export const NO_STOCK = "No valid stock — all batches expired or sold out";

export class PricingError extends Error {}

export function toMinor(value, dp) {
  let s = String(value ?? "").trim();
  if (s === "" || s === "." || s === "-" || !/^-?\d*\.?\d*$/.test(s)) throw new PricingError("Invalid amount");
  const neg = s.startsWith("-");
  if (neg) s = s.slice(1);
  const [ip, fp = ""] = s.split(".");
  const pad = (fp + "0".repeat(dp + 1)).slice(0, dp + 1);
  let n = BigInt(ip || "0") * 10n ** BigInt(dp) + BigInt(pad.slice(0, dp) || "0");
  if (Number(pad[dp]) >= 5) n += 1n;
  return Number(neg ? -n : n);
}

export const rateBp = (rate) => toMinor(rate, 2);
export const roundDiv = (n, d) => Math.floor((2 * n + d) / (2 * d));

export function minorToStr(n, dp) {
  const neg = n < 0;
  let s = String(Math.abs(n));
  if (dp === 0) return (neg ? "-" : "") + s;
  s = s.padStart(dp + 1, "0");
  return `${neg ? "-" : ""}${s.slice(0, -dp)}.${s.slice(-dp)}`;
}

export function formatMoney(n, dp, currency) {
  const [i, f] = minorToStr(n, dp).split(".");
  const grouped = i.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const body = f ? `${grouped}.${f}` : grouped;
  return currency ? `${currency} ${body}` : body;
}

export function discountAmount(base, disc, dp) {
  if (!disc || disc.value === "" || disc.value == null || Number(disc.value) === 0) return 0;
  let amt;
  if (disc.type === "pct") {
    const bp = rateBp(disc.value);
    if (bp < 0) throw new PricingError("Invalid discount");
    if (bp > 10000) throw new PricingError(DISCOUNT_ERR);
    amt = roundDiv(base * bp, 10000);
  } else {
    amt = toMinor(disc.value, dp);
    if (amt < 0) throw new PricingError("Invalid discount");
  }
  if (amt > base) throw new PricingError(DISCOUNT_ERR);
  return amt;
}

export function priceCart(lines, cartDiscount, dp, inclusive) {
  const out = lines.map((ln) => {
    const gross = ln.unit_minor * ln.qty;
    const ld = discountAmount(gross, ln.discount, dp);
    return { gross, line_discount: ld, net: gross - ld, rate_bp: ln.rate_bp };
  });
  const subtotal = out.reduce((a, x) => a + x.net, 0);
  const cd = discountAmount(subtotal, cartDiscount, dp);
  const allocs = out.map(() => 0);
  if (cd > 0 && subtotal > 0) {
    const C = BigInt(cd), S = BigInt(subtotal);
    const rems = out.map((x, i) => {
      const p = C * BigInt(x.net);
      allocs[i] = Number(p / S);
      return [p % S, i];
    });
    let left = cd - allocs.reduce((a, b) => a + b, 0);
    rems.sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] > b[0] ? -1 : 1));
    for (let k = 0; k < left; k++) allocs[rems[k][1]] += 1;
  }
  const taxes = {};
  out.forEach((x, i) => {
    x.cart_alloc = allocs[i];
    x.taxable = x.net - allocs[i];
    const bp = x.rate_bp;
    x.tax = inclusive ? roundDiv(x.taxable * bp, 10000 + bp) : roundDiv(x.taxable * bp, 10000);
    taxes[bp] = (taxes[bp] || 0) + x.tax;
  });
  const tax_total = out.reduce((a, x) => a + x.tax, 0);
  const taxable_total = out.reduce((a, x) => a + x.taxable, 0);
  return {
    lines: out,
    gross_total: out.reduce((a, x) => a + x.gross, 0),
    line_discount_total: out.reduce((a, x) => a + x.line_discount, 0),
    subtotal,
    cart_discount: cd,
    tax_total,
    tax_breakdown: Object.keys(taxes).map(Number).sort((a, b) => a - b).map((k) => ({ rate_bp: k, amount: taxes[k] })),
    total: inclusive ? taxable_total : taxable_total + tax_total,
  };
}

export function fefoAllocate(batches, qty, today) {
  const valid = (batches || [])
    .filter((b) => b.expiry >= today && b.qty > 0)
    .sort((a, b) => (a.expiry === b.expiry ? a.batch_no.localeCompare(b.batch_no) : a.expiry < b.expiry ? -1 : 1));
  const allocations = [];
  let need = qty;
  for (const b of valid) {
    if (need <= 0) break;
    const take = Math.min(need, b.qty);
    allocations.push({ batch_id: b.id, batch_no: b.batch_no, expiry: b.expiry, qty: take });
    need -= take;
  }
  return { allocations, available: valid.reduce((a, b) => a + b.qty, 0), ok: need <= 0 };
}

export const bpToPct = (bp) => {
  const s = minorToStr(bp, 2);
  return s.replace(/\.?0+$/, "");
};

export const fmtExpiry = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// Inventory add-on: FEFO (pharmacy) / FIFO (retail) across shelf lots; ties go to the lower location code. Mirrors backend inventory.pick.
export function pickLots(product, qty, today, pharmacy) {
  const lots = product.lots || [];
  const cand = lots
    .filter((l) => l.qty > 0 && (!pharmacy || l.expiry >= today))
    .sort((a, b) => {
      const ka = pharmacy ? a.expiry : a.received_at, kb = pharmacy ? b.expiry : b.received_at;
      return ka === kb ? a.location_code.localeCompare(b.location_code) : ka < kb ? -1 : 1;
    });
  const allocations = [];
  let need = qty;
  for (const l of cand) {
    if (need <= 0) break;
    const take = Math.min(need, l.qty);
    need -= take;
    allocations.push(pharmacy ? { lot_id: l.id, location_code: l.location_code, qty: take, batch_id: l.batch_id, batch_no: l.batch_no, expiry: l.expiry } : { lot_id: l.id, location_code: l.location_code, qty: take });
  }
  if (need > 0 && !pharmacy && (product.stock != null || lots.length)) {
    const un = lots.find((l) => l.location_code === "UNASSIGNED");
    allocations.push({ lot_id: un ? un.id : null, location_code: "UNASSIGNED", qty: need });
    need = 0;
  }
  return { allocations, available: cand.reduce((a, l) => a + l.qty, 0), ok: need <= 0 };
}

export const mmYYYY = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
export const pickLabel = (a) => (a.batch_no ? `Pick: ${a.location_code} · Batch ${a.batch_no} · exp ${mmYYYY(a.expiry)}` : `Pick: ${a.location_code}`);
